import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff, Phone } from "lucide-react";
import SupplierForm from "@/components/SupplierForm";
const LoginPage = () => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  
  const {
    signIn,
    signUp,
    signInWithProvider,
    user,
    userRole
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form fields
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get redirect parameter and decode it
  const getRedirectUrl = () => {
    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      try {
        return decodeURIComponent(redirectParam);
      } catch (error) {
        console.error('Error decoding redirect parameter:', error);
        return "/";
      }
    }
    return "/";
  };

  const redirectTarget = getRedirectUrl();

  useEffect(() => {
    if (searchParams.get('redirect')) {
      localStorage.removeItem('loginOrigin');
    }
  }, [searchParams]);

  // If user is already logged in, redirect to appropriate page
  if (user && userRole) {
    const postLoginRedirect = localStorage.getItem('postLoginRedirect');
    const redirectParam = searchParams.get('redirect');
    const candidateRedirect = redirectParam ? redirectTarget : postLoginRedirect;

    // Validate stored/param redirect against current role to avoid loops
    // (e.g. stale postLoginRedirect=/fornecedor/... when logging in as admin)
    const isRedirectAllowedForRole = (url: string | null): boolean => {
      if (!url || url.startsWith('/login')) return false;
      if (url.startsWith('/admin')) return userRole === 'admin';
      if (url.startsWith('/fornecedor')) return userRole === 'supplier';
      return true;
    };

    if (isRedirectAllowedForRole(candidateRedirect)) {
      localStorage.removeItem('postLoginRedirect');
      return <Navigate to={candidateRedirect!} replace />;
    }

    // Clear stale redirect that doesn't match the role
    localStorage.removeItem('postLoginRedirect');

    let redirectUrl = '/';
    if (userRole === 'admin') {
      redirectUrl = '/admin';
    } else if (userRole === 'supplier') {
      redirectUrl = '/fornecedor/painel/inicio';
    } else if (userRole === 'client') {
      redirectUrl = '/';
    }
    return <Navigate to={redirectUrl} replace />;
  }

  // Validation for registration
  const emailsMatch = email === confirmEmail;
  const passwordsMatch = password === confirmPassword;
  const isRegistrationValid = !isLoginView && emailsMatch && passwordsMatch && email && password && fullName.trim() && phone.trim();
  const isFormValid = isLoginView || isRegistrationValid;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLoginView) {
        // Handle login
        const redirectParam = searchParams.get('redirect');
        if (redirectParam) {
          localStorage.setItem('postLoginRedirect', redirectTarget);
        }
        const origin = redirectParam ? undefined : (searchParams.get('from') || 'home');
        const {
          error,
          requiresPasswordChange
        } = await signIn(email, password, origin);
        if (error) {
          toast.error("Erro ao fazer login", {
            description: error.message || "Verifique suas credenciais e tente novamente."
          });
        } else if (requiresPasswordChange) {
          navigate('/nova-senha', { replace: true });
        }
        // Navigation will be handled by AuthContext after role is fetched
      } else {
        // Handle registration
        if (!fullName.trim()) {
          toast.error("Nome é obrigatório", {
            description: "Por favor, informe seu nome completo."
          });
          setLoading(false);
          return;
        }
        const {
          error
        } = await signUp(email, password, fullName, phone);
        if (error) {
          toast.error("Erro ao criar conta", {
            description: error.message || "Verifique suas informações e tente novamente."
          });
        } else {
          toast.success("Conta criada com sucesso", {
            description: "Verifique seu e-mail para confirmar sua conta."
          });
          setIsLoginView(true);
        }
      }
    } catch (error: any) {
      toast.error("Ocorreu um erro", {
        description: error.message || "Tente novamente mais tarde."
      });
    }
    setLoading(false);
  };
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        localStorage.setItem('postLoginRedirect', redirectTarget);
      }
      // Don't remove postLoginRedirect if it was already set by another page (e.g., InfosFesta)

      const origin = redirectParam ? undefined : (searchParams.get('from') || 'home');
      if (origin) {
        localStorage.setItem('loginOrigin', origin);
        localStorage.setItem('loginOriginAt', String(Date.now()));
      }
      const { error } = await signInWithProvider('google');
      if (error) {
        toast.error("Erro ao fazer login com Google", {
          description: error.message || "Tente novamente mais tarde."
        });
      }
    } catch (error: any) {
      toast.error("Erro no login social", {
        description: error.message || "Tente novamente mais tarde."
      });
    }
    setLoading(false);
  };
  return <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
        <div className="container-custom">
          <div className="max-w-md mx-auto">
            <Card className="border-none shadow-md">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold mb-2">
                  {isLoginView ? "Entre na sua conta" : "Crie sua conta"}
                </CardTitle>
                <CardDescription>
                  {isLoginView ? "Acesse sua conta para gerenciar seus eventos" : "Cadastre-se para organizar eventos incríveis"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLoginView && <div className="space-y-2">
                      <Label htmlFor="name">Nome completo</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="name" type="text" placeholder="Seu nome completo" required className="pl-10 focus:border-primary transition-colors" value={fullName} onChange={e => setFullName(e.target.value)} />
                      </div>
                    </div>}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input id="email" type="email" placeholder="seu@email.com" required className="pl-10 focus:border-primary transition-colors" value={email} onChange={e => setEmail(e.target.value)} />
                    </div>
                  </div>

                  {!isLoginView && <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="phone" type="tel" placeholder="(11) 99999-9999" required className="pl-10 focus:border-primary transition-colors" value={phone} onChange={e => {
                      const value = e.target.value.replace(/\D/g, '');
                      const formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                      if (value.length <= 11) {
                        setPhone(formatted);
                      }
                    }} maxLength={15} />
                      </div>
                    </div>}

                  {!isLoginView && <div className="space-y-2">
                      <Label htmlFor="confirmEmail">Confirmar e-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="confirmEmail" type="email" placeholder="Confirme seu e-mail" required className={`pl-10 transition-colors ${confirmEmail && !emailsMatch ? "border-red-500 focus:border-red-500" : "focus:border-primary"}`} value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} />
                      </div>
                      {confirmEmail && !emailsMatch && <p className="text-xs text-red-500">Os e-mails não coincidem</p>}
                    </div>}
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Senha</Label>
                      {isLoginView && <Link to="/esqueci-senha" className="text-sm text-primary hover:text-primary/80">
                          Esqueceu a senha?
                        </Link>}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                      <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" required className="pl-10 pr-10 focus:border-primary transition-colors" value={password} onChange={e => setPassword(e.target.value)} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {!isLoginView && <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirmar senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                        <Input id="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="Confirme sua senha" required className={`pl-10 pr-10 transition-colors ${confirmPassword && !passwordsMatch ? "border-red-500 focus:border-red-500" : "focus:border-primary"}`} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground hover:text-foreground transition-colors">
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      {confirmPassword && !passwordsMatch && <p className="text-xs text-red-500">As senhas não coincidem</p>}
                    </div>}
                  
                  {isLoginView && <div className="flex items-center space-x-2">
                      <Checkbox id="remember" className="data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                      <label htmlFor="remember" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        Lembrar de mim
                      </label>
                    </div>}
                  
                  <Button type="submit" className="w-full" disabled={loading || !isLoginView && !isFormValid}>
                    {loading ? <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isLoginView ? "Entrando..." : "Criando conta..."}
                      </span> : isLoginView ? "Entrar" : "Cadastrar"}
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <p className="text-sm">
                    {isLoginView ? "Não tem uma conta?" : "Já tem uma conta?"}{" "}
                    {isLoginView ? (
                      <button type="button" onClick={() => setShowSupplierForm(true)} className="text-primary font-medium hover:text-primary/80 transition-colors">
                        Conheça Grátis
                      </button>
                    ) : (
                      <button type="button" onClick={() => setIsLoginView(true)} className="text-primary font-medium hover:text-primary/80 transition-colors">
                        Entre
                      </button>
                    )}
                  </p>
                </div>
                
                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-muted-foreground">ou continue com</span>
                  </div>
                </div>
                
                <div className="mt-6">
                  <Button variant="outline" type="button" className="w-full flex items-center justify-center h-12" onClick={handleGoogleLogin} disabled={loading}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-5 w-5 mr-3">
                      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                    </svg>
                    Continuar com Google
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
      <SupplierForm isOpen={showSupplierForm} onClose={() => setShowSupplierForm(false)} />
    </div>;
};
export default LoginPage;